import { WishlistButton } from '~/features/wishlist/components/WishlistButton/WishlistButton';
import { useWishlistHandlers } from '~/features/wishlist/hooks/useWishlistHandlers';
import { IVariationDetails } from '~/lib/data-contract';
import CampaignTimeframe from '~/shared/components/CampaignTimeframe/CampaignTimeframe';
import Link from '~/shared/components/Link/Link';
import { Text } from '~/shared/components/Text/Text';
import getHasFreeShipping from '~/shared/hooks/getHasFreeShipping';
import { useFormattedPrice } from '~/shared/hooks/useFormattedPrice';
import useShippingPriceMap from '~/shared/hooks/useShippingPriceMap';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';

import styles from './productDetailsSummary.module.css';

import { BadgeList } from '../../../ProductCard/components/BadgeList/BadgeList';
import { ProductDetailsAttrList } from '../ProductDetailsAttrList/ProductDetailsAttrList';
import { ProductDetailsPrice } from '../ProductDetailsPrice/ProductDetailsPrice';

export type ProductDetailsSummaryProps = {
	variation: IVariationDetails;
	deliveryUrl?: string;
};

export const ProductDetailsSummary = ({ variation, deliveryUrl }: ProductDetailsSummaryProps) => {
	const { translate } = useTranslation();
	const { isInWishlist, handleWishlistClick } = useWishlistHandlers(variation);
	const {
		data: { market: { packagingSurcharge = 0 } = {} },
	} = useFrame();

	const shippingPriceMap = useShippingPriceMap();
	const shippingPrice = useFormattedPrice(shippingPriceMap?.rate, true);
	const { discountedPriceValidFrom, discountedPriceValidUntil, showSchedulingAndStatusOnProductCardOptions = 0 } = variation;

	const showCampaignTimeframeDate = showSchedulingAndStatusOnProductCardOptions === 1;
	const showCampaignTimeframeUntilSoldOut = showSchedulingAndStatusOnProductCardOptions === 2;

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<span className={styles.summaryLabel}>
					{variation.newArrival ? <span className={styles.newProductBadge}>{translate('product-card.news-badge')}</span> : null}
					{variation.color?.mainColorName}
					{variation.color?.showColorNameOnPdp && `, ${variation.color.colorName}`}
				</span>
				{variation.designCollection?.name ? <span className={styles.summaryLabel}>{variation.designCollection.name}</span> : null}
				<Text
					as={'h1'}
					variant={'display3'}
					marginBottom="2"
				>
					{variation.name}
				</Text>
				{variation.usps.length > 0 ? (
					<ProductDetailsAttrList
						size="lg"
						attrs={variation.usps}
					/>
				) : null}
			</header>
			<div>
				{variation.splashes ? (
					<BadgeList
						badges={variation.splashes}
						className={styles.badges}
					/>
				) : null}
				<div className={styles.priceContainer}>
					<ProductDetailsPrice
						price={variation.price}
						discountedPrice={variation.discountedPrice}
					/>
					<WishlistButton
						active={isInWishlist}
						onClick={handleWishlistClick}
					/>
					{showCampaignTimeframeDate || showCampaignTimeframeUntilSoldOut ? (
						<CampaignTimeframe
							timeFrame={
								showCampaignTimeframeDate
									? {
											fromDateString: discountedPriceValidFrom,
											untilDateString: discountedPriceValidUntil,
									  }
									: undefined
							}
						/>
					) : null}
					{packagingSurcharge ? <span className={styles.shippingLabel}>{translate('product.plus-packaging-surcharge')}</span> : null}
					{shippingPrice ? (
						<span className={styles.shippingLabel}>
							{shippingPriceMap && getHasFreeShipping(variation, shippingPriceMap)
								? translate('product.shipping-price.label-free')
								: translate('product.shipping-price.label', {
										price: shippingPrice,
								  })}
							{deliveryUrl ? (
								<>
									<br />
									<Link
										href={deliveryUrl}
										target="_blank"
									>
										{translate('product.delivery-link')}
									</Link>
								</>
							) : null}
						</span>
					) : null}
				</div>
			</div>
		</div>
	);
};
